import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { identityUserService, identityRoleService, permissionService } from '@/services/identity/identityService';
import {
  IdentityUser,
  CreateIdentityUser,
  UpdateIdentityUser,
  IdentityRole,
  CreateIdentityRole,
  UpdateIdentityRole,
  PermissionGroup,
  UpdatePermissions,
  UserLookup,
} from '@/types/identity/Identity';

// --- Query Keys ---

export const identityKeys = {
  users: {
    all: ['identity-users'] as const,
    lists: () => [...identityKeys.users.all, 'list'] as const,
    details: () => [...identityKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...identityKeys.users.details(), id] as const,
    roles: (id: string) => [...identityKeys.users.all, 'roles', id] as const,
    assignableRoles: () => [...identityKeys.users.all, 'assignable-roles'] as const,
    search: (filter: string) => [...identityKeys.users.all, 'search', filter] as const,
  },
  roles: {
    all: ['identity-roles'] as const,
    lists: () => [...identityKeys.roles.all, 'list'] as const,
    list: (filter?: string) => [...identityKeys.roles.lists(), { filter }] as const,
    allRoles: () => [...identityKeys.roles.all, 'all'] as const,
    details: () => [...identityKeys.roles.all, 'detail'] as const,
    detail: (id: string) => [...identityKeys.roles.details(), id] as const,
  },
  permissions: {
    role: (roleName: string) => ['permissions', 'role', roleName] as const,
    user: (userId: string) => ['permissions', 'user', userId] as const,
  },
};

// --- User Hooks ---

export function useUser(
  id: string,
  options?: Omit<UseQueryOptions<IdentityUser, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<IdentityUser, Error>({
    queryKey: identityKeys.users.detail(id),
    queryFn: () => identityUserService.getById(id),
    enabled: !!id && id !== 'new',
    ...options,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation<IdentityUser, Error, CreateIdentityUser>({
    mutationFn: (data) => identityUserService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: identityKeys.users.all });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<IdentityUser, Error, { id: string; data: UpdateIdentityUser }>({
    mutationFn: ({ id, data }) => identityUserService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: identityKeys.users.detail(id) });
      queryClient.invalidateQueries({ queryKey: identityKeys.users.lists() });
    },
  });
}

export function useUserRoles(userId: string) {
  return useQuery({
    queryKey: identityKeys.users.roles(userId),
    queryFn: () => identityUserService.getUserRoles(userId),
    enabled: !!userId && userId !== 'new',
    select: (data) => data.items.map((r) => r.name),
  });
}

export function useAssignableRoles() {
  return useQuery({
    queryKey: identityKeys.users.assignableRoles(),
    queryFn: () => identityUserService.getAssignableRoles(),
    select: (data) => data.items,
  });
}

export function useUserSearch(filter: string, enabled = true) {
  return useQuery<UserLookup[], Error>({
    queryKey: identityKeys.users.search(filter),
    // No minimum length — an empty filter returns the first 25 users, so a default page shows on
    // open with no typing; typing from the first character narrows it.
    queryFn: () => identityUserService.search(filter, 25),
    enabled,
  });
}

// --- Role Hooks ---
// The Roles grid itself reads /api/identity/role-list through roleListService (EntityListView's
// server-side data source), so there is no list hook here.

export function useAllRoles() {
  return useQuery<IdentityRole[], Error>({
    queryKey: identityKeys.roles.allRoles(),
    queryFn: () => identityRoleService.getAll(),
  });
}

export function useRole(
  id: string,
  options?: Omit<UseQueryOptions<IdentityRole, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<IdentityRole, Error>({
    queryKey: identityKeys.roles.detail(id),
    queryFn: () => identityRoleService.getById(id),
    enabled: !!id && id !== 'new',
    ...options,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation<IdentityRole, Error, CreateIdentityRole>({
    mutationFn: (data) => identityRoleService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: identityKeys.roles.all });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation<IdentityRole, Error, { id: string; data: UpdateIdentityRole }>({
    mutationFn: ({ id, data }) => identityRoleService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: identityKeys.roles.detail(id) });
      queryClient.invalidateQueries({ queryKey: identityKeys.roles.lists() });
      queryClient.invalidateQueries({ queryKey: identityKeys.roles.allRoles() });
    },
  });
}

// --- Permission Hooks ---

export function useRolePermissions(roleName: string) {
  return useQuery<{ groups: PermissionGroup[] }, Error>({
    queryKey: identityKeys.permissions.role(roleName),
    queryFn: () => permissionService.getRolePermissions(roleName),
    enabled: !!roleName,
  });
}

export function useUpdateRolePermissions(roleName: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, UpdatePermissions>({
    mutationFn: (data) => permissionService.updateRolePermissions(roleName, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: identityKeys.permissions.role(roleName) });
    },
  });
}
